import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { OrderType, PositionsSortByType } from '@/shared/backend/types/leaderboard'

import { Button } from 'components/Button/Button'
import { IconClose } from 'components/Icons/IconClose'
import { IconFilterTrigger } from 'components/Icons/IconFilter'
import { Menu } from 'components/Menu/MenuWithPortal'
import { Typography } from 'components/Typography/Typography'
import { useOnClickOutsideMultiple } from 'utils/onClickOutside'
import { Option, ReactSelect } from 'components/ReactSelect/ReactSelect'
import { timeFilterOptions, positionsSortMobileOptions } from '../const'
import Checkbox from 'components/Checkbox/Checkbox'

const MenuTrigger = styled.div`
  display: flex;
`

const MenuContainer = styled.div`
  padding: 16px;
  width: 280px;
`

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px solid ${p => p.theme.colors.text.primary};
`

const Row = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: space-between;
`

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  gap: 16px;
`

const WrapContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`

const CheckboxContainer = styled.div`
  display: inline-flex;
  margin-right: 32px;
  align-items: center;
  cursor: pointer;
`

interface Props {
  sortBy: PositionsSortByType
  initialTimeFilter: Option
  sortOrder: OrderType
  initialShowClosedPositions: boolean
  initialShowStable: boolean
  initialShowVolatile: boolean
  onSubmit: (
    newValue: Option,
    selectedSortOption: string,
    showClosedPositions: boolean,
    showStable: boolean,
    showVolatile: boolean
  ) => void
}

export const PositionsFilters = ({
  initialTimeFilter,
  onSubmit,
  sortBy,
  sortOrder,
  initialShowClosedPositions,
  initialShowStable,
  initialShowVolatile,
}: Props) => {
  const [isMenuOpen, setMenuOpen] = useState(false)
  const [selectedTimeFilter, setSelectedTimeFilter] = useState(initialTimeFilter)
  const [selectedSortOption, setSelectedSortOption] = useState(() => {
    const initialSortOption = positionsSortMobileOptions.find(o => o.value === `${sortBy} (${sortOrder})`)
    if (!initialSortOption) {
      return positionsSortMobileOptions[0]
    }
    return initialSortOption
  })
  const [showClosedPositions, setShowClosedPositions] = useState(initialShowClosedPositions)
  const [showStable, setShowStable] = useState(initialShowStable)
  const [showVolatile, setShowVolatile] = useState(initialShowVolatile)

  const menuTriggerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setSelectedTimeFilter(initialTimeFilter)
  }, [initialTimeFilter])

  useEffect(() => {
    const initialSortOption = positionsSortMobileOptions.find(o => o.value === `${sortBy} (${sortOrder})`)
    setSelectedSortOption(initialSortOption || positionsSortMobileOptions[0])
  }, [sortBy, sortOrder])

  const onClose = useCallback(() => setMenuOpen(false), [])

  const toggleMenu = useCallback(() => {
    setMenuOpen(!isMenuOpen)
    setSelectedTimeFilter({ ...selectedTimeFilter })
  }, [isMenuOpen, selectedTimeFilter])

  const handleSaveClick = useCallback(() => {
    setMenuOpen(!isMenuOpen)
    onSubmit(selectedTimeFilter, selectedSortOption.value, showClosedPositions, showStable, showVolatile)
  }, [isMenuOpen, selectedTimeFilter, selectedSortOption, showClosedPositions, showStable, showVolatile])

  const handleSelectTimeFilterChange = useCallback((newValue: Option) => {
    setSelectedTimeFilter(newValue)
  }, [])

  const handleSortChange = useCallback((newValue: Option) => {
    setSelectedSortOption(newValue)
  }, [])

  const handleShowClosedPositionsChange = useCallback(() => {
    setShowClosedPositions(!showClosedPositions)
  }, [showClosedPositions])

  const handleShowStableChange = useCallback(() => {
    setShowStable(!showStable)
    if (!showStable) {
      setShowVolatile(false)
    }
  }, [showStable])

  const handleShowVolatileChange = useCallback(() => {
    setShowVolatile(!showVolatile)
    if (!showVolatile) {
      setShowStable(false)
    }
  }, [showStable])

  useOnClickOutsideMultiple([menuTriggerRef, menuRef], onClose)

  return (
    <div style={{ position: 'relative' }}>
      <MenuTrigger onClick={toggleMenu} ref={menuTriggerRef}>
        <IconFilterTrigger />
      </MenuTrigger>
      {isMenuOpen && (
        <Menu ref={menuRef} anchorElement={menuTriggerRef} style={{ marginLeft: -280, marginTop: -30 }}>
          <MenuContainer>
            <Title>
              <Typography variant="description" fontWeight="medium">
                Filters
              </Typography>
              <div onClick={toggleMenu} style={{ display: 'flex', cursor: 'pointer' }}>
                <IconClose />
              </div>
            </Title>
            <Row>
              <div style={{ width: 'calc(60% - 5px)' }}>
                <ReactSelect
                  selectedOption={selectedSortOption}
                  options={positionsSortMobileOptions}
                  onSelect={handleSortChange}
                />
              </div>
              <div style={{ width: 'calc(40% - 5px)' }}>
                <ReactSelect
                  options={timeFilterOptions}
                  selectedOption={selectedTimeFilter}
                  onSelect={handleSelectTimeFilterChange}
                />
              </div>
            </Row>
            <Row>
              <WrapContainer>
                <Button outlined={!showStable} onClick={handleShowStableChange}>
                  Stable
                </Button>
                <Button outlined={!showVolatile} onClick={handleShowVolatileChange}>
                  Volatile
                </Button>
                <CheckboxContainer onClick={handleShowClosedPositionsChange}>
                  <Checkbox checked={showClosedPositions} />
                  <Typography variant="body" color="secondary" style={{ marginLeft: 6 }}>
                    Show closed positions
                  </Typography>
                </CheckboxContainer>
              </WrapContainer>
            </Row>
            <Actions>
              <Button outlined style={{ width: '50%', height: 36, padding: 0 }} onClick={toggleMenu}>
                Cancel
              </Button>
              <Button style={{ width: '50%', height: 36, padding: 0 }} onClick={handleSaveClick}>
                Submit
              </Button>
            </Actions>
          </MenuContainer>
        </Menu>
      )}
    </div>
  )
}
